import { EllipsisVerticalIcon } from "@heroicons/react/16/solid";
import { get } from "lodash-es";
import React, { FC, useRef, useState } from "react";

import { Container, MenuItem, MenuList, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import {
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";

import DataGridPro from "../../components/DataGridPro";
import DataGridProToolbar from "../../components/DataGridProToolbar";
import Link from "../../components/styled/Link";
import SvgIcon from "../../components/styled/SvgIcon";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import usePaginationModel from "../../hooks/use-pagination-model";
import { usePopover } from "../../hooks/use-popover";
import { useSavedColumnWidths } from "../../hooks/use-saved-column-widths";
import useUserInfoColumn from "../../hooks/use-user-info-column";
import { useSelector } from "../../store";
import {
  getFilter,
  getOrderBy,
  useListActivityLogsQuery,
} from "../../store/enhancedApi";
import { ActivityLog } from "../../store/pomeriumApi";
import { getEntityUrl } from "../../utils/activity-log-urls";
import { updatedAt } from "../../utils/data-grid-col-defs";

const ChangesList: FC = () => {
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const actionsPopover = usePopover();
  const anchorRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigateWithSearch();
  const [row, setRow] = useState<ActivityLog>();
  const { mutateColumnWidths, onColumnWidthChange } =
    useSavedColumnWidths("changes");
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const {
    data: result,
    isLoading,
    isFetching,
  } = useListActivityLogsQuery({
    organizationId,
    namespaceId: clusterId,
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    filter: getFilter(filterModel),
    orderBy: getOrderBy(sortModel),
  });
  const activityLogs = result?.rows || [];
  const rowCount = result?.rowCount || 0;
  const estimatedRowCount = result?.estimatedRowCount || 0;

  const idToUrl = activityLogs?.reduce(
    (o, log) => ({ ...o, [log.id]: getEntityUrl(log) }),
    {},
  );

  const changedBy = useUserInfoColumn("Changed By");
  let columns: GridColDef<ActivityLog>[] = [
    {
      display: "flex",
      headerName: "Resource ID",
      field: "entity.id",
      flex: 1,
      valueGetter(_value, row) {
        return row?.entity?.id;
      },
      filterOperators: getGridStringOperators(true)
        .filter((op) => op.value === "equals")
        .map((op) => ({ ...op, value: "is" })),
    },
    {
      display: "flex",
      headerName: "Name",
      field: "entity.data.name",
      flex: 1,
      valueGetter(_value, row) {
        return (
          row.entity?.data?.name ||
          row.entity?.data?.description ||
          row.entity?.data?.domainName ||
          ""
        );
      },
      renderCell: function NameCell(params: GridCellParams) {
        const url = get(idToUrl, params.row.id);
        if (url) {
          return (
            <Link to={url}>
              {params?.row.entity?.data?.name ||
                params?.row.entity?.data?.description ||
                ""}
            </Link>
          );
        } else {
          return (
            params?.row.entity?.data?.name ||
            params?.row.entity?.data?.description ||
            params?.row?.entity?.data?.domainName ||
            "entity"
          );
        }
      },
    },
    {
      display: "flex",
      headerName: "Type",
      field: "entity.type",
      flex: 1,
      valueGetter(_value, row) {
        return row?.entity?.type;
      },
    },
    changedBy,
    updatedAt,
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: function action(params) {
        return (
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              onClick={(e) => {
                anchorRef.current = e.currentTarget;
                setRow(params.row);
                actionsPopover.handleOpen();
              }}
              sx={{ width: 4 }}
            >
              <SvgIcon color="disabled">
                <EllipsisVerticalIcon />
              </SvgIcon>
            </IconButton>
          </Grid>
        );
      },
    },
  ];
  columns = mutateColumnWidths(columns);

  return (
    <>
      <Popover
        anchorEl={anchorRef.current}
        disableScrollLock
        onClose={actionsPopover.handleClose}
        open={actionsPopover.open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
            },
          },
        }}
      >
        <MenuList>
          <MenuItem
            onClick={() =>
              navigate({
                pathname: [
                  "/app/reports/deployments/diff",
                  row?.entity?.type,
                  row?.entity?.id,
                ].join("/"),
                search:
                  "?" +
                  new URLSearchParams({
                    to: row?.id || "",
                  }).toString(),
              })
            }
          >
            Compare
          </MenuItem>
          {get(idToUrl, row?.id || "", "") && (
            <MenuItem onClick={() => navigate(get(idToUrl, row?.id || "", ""))}>
              View / Edit
            </MenuItem>
          )}
        </MenuList>
      </Popover>
      <Container component={Paper} maxWidth={false} disableGutters={true}>
        <DataGridPro
          checkboxSelection={false}
          loading={isLoading || isFetching}
          rows={activityLogs || []}
          columns={columns}
          slots={{
            toolbar: DataGridProToolbar,
          }}
          onColumnWidthChange={onColumnWidthChange}
          paginationMode="server"
          rowCount={rowCount}
          estimatedRowCount={estimatedRowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={setSortModel}
          onFilterModelChange={setFilterModel}
        />
      </Container>
    </>
  );
};
export default ChangesList;
